import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { qualityQuestionsApi } from '@/services/api';
import { QualityQuestion, QualityQuestionOption, QualityQuestionType } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { ShieldAlert, Plus, Pencil, Ban, CheckCircle, ListChecks, MessageSquare } from 'lucide-react';

const newOptionId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `opt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

type FormState = {
  type: QualityQuestionType;
  prompt: string;
  options: QualityQuestionOption[];
  correctOptionId: string;
  isActive: boolean;
};

const emptyForm = (): FormState => {
  const a = newOptionId();
  const b = newOptionId();
  return {
    type: 'multiple_choice',
    prompt: '',
    options: [
      { id: a, label: '' },
      { id: b, label: '' },
    ],
    correctOptionId: a,
    isActive: true,
  };
};

const formFromQuestion = (q: QualityQuestion): FormState => {
  if (q.type === 'multiple_choice') {
    const options =
      q.options && q.options.length >= 2
        ? q.options.map((o) => ({ id: o.id, label: o.label }))
        : emptyForm().options;
    return {
      type: 'multiple_choice',
      prompt: q.prompt,
      options,
      correctOptionId: q.correctOptionId || options[0].id,
      isActive: q.isActive,
    };
  }
  return {
    type: 'open_ended',
    prompt: q.prompt,
    options: [],
    correctOptionId: '',
    isActive: q.isActive,
  };
};

const QualityQuestionsPage = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<QualityQuestion | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_admin';

  const { data: response, isLoading } = useQuery(
    ['quality-questions', showInactive],
    () => qualityQuestionsApi.getAll(showInactive ? undefined : { isActive: true }),
    {
      enabled: isAdmin,
      onError: () => setError('Failed to load quality questions'),
    }
  );

  const questions: QualityQuestion[] = response?.data || [];

  const createMutation = useMutation(
    (data: Parameters<typeof qualityQuestionsApi.create>[0]) => qualityQuestionsApi.create(data),
    {
      onSuccess: () => {
        setShowModal(false);
        setEditing(null);
        setForm(emptyForm());
        queryClient.invalidateQueries('quality-questions');
      },
      onError: (err: any) => {
        setError(err.response?.data?.error || 'Failed to create question');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Parameters<typeof qualityQuestionsApi.update>[1] }) =>
      qualityQuestionsApi.update(id, data),
    {
      onSuccess: () => {
        setShowModal(false);
        setEditing(null);
        setForm(emptyForm());
        queryClient.invalidateQueries('quality-questions');
      },
      onError: (err: any) => {
        setError(err.response?.data?.error || 'Failed to update question');
      },
    }
  );

  const deactivateMutation = useMutation(
    (id: string) => qualityQuestionsApi.deactivate(id),
    {
      onSuccess: () => queryClient.invalidateQueries('quality-questions'),
      onError: (err: any) => {
        setError(err.response?.data?.error || 'Failed to deactivate question');
      },
    }
  );

  const reactivateMutation = useMutation(
    (id: string) => qualityQuestionsApi.update(id, { isActive: true }),
    {
      onSuccess: () => queryClient.invalidateQueries('quality-questions'),
      onError: (err: any) => {
        setError(err.response?.data?.error || 'Failed to reactivate question');
      },
    }
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setError('');
    setShowModal(true);
  };

  const openEdit = (q: QualityQuestion) => {
    setEditing(q);
    setForm(formFromQuestion(q));
    setError('');
    setShowModal(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.prompt.trim()) {
      setError('Prompt is required');
      return;
    }

    if (form.type === 'multiple_choice') {
      const labels = form.options.map((o) => o.label.trim()).filter(Boolean);
      if (labels.length < 2) {
        setError('Coded response questions need at least 2 options');
        return;
      }
      if (!form.options.some((o) => o.id === form.correctOptionId && o.label.trim())) {
        setError('Select a correct answer from the options');
        return;
      }
    }

    const payload =
      form.type === 'multiple_choice'
        ? {
            type: 'multiple_choice' as const,
            prompt: form.prompt.trim(),
            options: form.options
              .filter((o) => o.label.trim())
              .map((o) => ({ id: o.id, label: o.label.trim() })),
            correctOptionId: form.correctOptionId,
            isActive: form.isActive,
          }
        : {
            type: 'open_ended' as const,
            prompt: form.prompt.trim(),
            options: null,
            correctOptionId: null,
            isActive: form.isActive,
          };

    if (editing) {
      updateMutation.mutate({ id: editing.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const setType = (type: QualityQuestionType) => {
    if (type === 'multiple_choice' && form.options.length < 2) {
      const next = emptyForm();
      setForm({ ...next, type, prompt: form.prompt, isActive: form.isActive });
    } else if (type === 'open_ended') {
      setForm((prev) => ({ ...prev, type, options: [], correctOptionId: '' }));
    } else {
      setForm((prev) => ({ ...prev, type }));
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-20">
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-red-50 flex items-center justify-center">
          <ShieldAlert className="w-8 h-8 text-[#EF4444]" />
        </div>
        <div className="text-[#EF4444] text-lg font-medium mb-2">Access Denied</div>
        <p className="text-[#6B7280] text-[15px]">Admin privileges required to manage quality questions.</p>
      </div>
    );
  }

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1F2B]">Quality Questions</h1>
          <p className="text-[#6B7280] mt-1 text-[15px]">
            Global trap / screener question bank for projects and collections
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-[#6B7280] font-medium">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-[#ECEEF1]"
            />
            Show inactive
          </label>
          <button type="button" onClick={openCreate} className="md-btn md-btn-primary text-[13px] px-4 py-2 inline-flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Question
          </button>
        </div>
      </div>

      {error && !showModal && (
        <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl">{error}</div>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 md-skeleton rounded-xl" />
          ))}
        </div>
      ) : questions.length === 0 ? (
        <div className="text-center py-16 bg-[#F9FAFB] rounded-xl border border-[#ECEEF1]">
          <ListChecks className="w-10 h-10 text-[#9CA3AF] mx-auto mb-3" />
          <p className="text-[#6B7280] mb-4">No quality questions yet. Create your first trap question.</p>
          <button type="button" onClick={openCreate} className="md-btn md-btn-primary text-[13px]">
            Create First Question
          </button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#ECEEF1]">
          <table className="w-full">
            <thead>
              <tr className="bg-[#F9FAFB] border-b border-[#ECEEF1]">
                <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Type</th>
                <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Prompt</th>
                <th className="text-left py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Status</th>
                <th className="text-right py-3 px-4 text-[13px] font-bold text-[#9CA3AF] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {questions.map((q) => (
                <tr key={q.id} className="border-b border-[#ECEEF1] hover:bg-[#F9FAFB] transition-colors">
                  <td className="py-3 px-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        q.type === 'multiple_choice'
                          ? 'bg-[#E8F4FD] text-[#0B7BC7] border border-[#B3DCFA]'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}
                    >
                      {q.type === 'multiple_choice' ? (
                        <ListChecks className="w-3.5 h-3.5" />
                      ) : (
                        <MessageSquare className="w-3.5 h-3.5" />
                      )}
                      {q.type === 'multiple_choice' ? 'Coded response' : 'Open-ended'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-[#374151] max-w-xl">
                    <span className="line-clamp-2">{q.prompt}</span>
                    {q.type === 'multiple_choice' && q.options && (
                      <span className="block text-xs text-[#9CA3AF] mt-1">
                        {q.options.length} options
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <span
                      className={`px-2.5 py-1 inline-flex text-xs font-semibold rounded-full ${
                        q.isActive
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : 'bg-[#F3F5F7] text-[#6B7280] border border-[#ECEEF1]'
                      }`}
                    >
                      {q.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(q)}
                        className="text-[13px] text-[#0B7BC7] hover:text-[#0D8FDE] font-semibold inline-flex items-center gap-1"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </button>
                      {q.isActive ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm('Deactivate this question? It will no longer appear in new assignments.')) {
                              deactivateMutation.mutate(q.id);
                            }
                          }}
                          className="text-[13px] text-[#EF4444] hover:text-red-600 font-semibold inline-flex items-center gap-1"
                        >
                          <Ban className="w-3.5 h-3.5" />
                          Deactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => reactivateMutation.mutate(q.id)}
                          className="text-[13px] text-emerald-600 hover:text-emerald-700 font-semibold inline-flex items-center gap-1"
                        >
                          <CheckCircle className="w-3.5 h-3.5" />
                          Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 w-full max-w-xl shadow-[0_24px_48px_rgba(0,0,0,0.12)] border border-[#ECEEF1] max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold font-heading gradient-text mb-6">
              {editing ? 'Edit Question' : 'Create Question'}
            </h2>

            {error && (
              <div className="text-red-700 text-sm bg-red-50 p-3 border border-red-200 rounded-xl mb-4">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Type</label>
                <div className="flex gap-3">
                  {(['multiple_choice', 'open_ended'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      className={`flex-1 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${
                        form.type === t
                          ? 'border-[#18A5F9] bg-[#E8F4FD] text-[#0B7BC7]'
                          : 'border-[#ECEEF1] bg-white text-[#6B7280] hover:border-[#B3DCFA]'
                      }`}
                    >
                      {t === 'multiple_choice' ? 'Coded response' : 'Open-ended'}
                    </button>
                  ))}
                </div>
                {form.type === 'open_ended' && (
                  <p className="text-xs text-[#9CA3AF] mt-2">
                    Open-ended answers are informational only and never fail the respondent.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">Prompt *</label>
                <div className="md-outlined-textfield">
                  <textarea
                    value={form.prompt}
                    onChange={(e) => setForm((prev) => ({ ...prev, prompt: e.target.value }))}
                    className="w-full min-h-[100px]"
                    placeholder="Enter the question shown to respondents"
                    required
                  />
                </div>
              </div>

              {form.type === 'multiple_choice' && (
                <div>
                  <label className="block text-[13px] font-bold text-[#374151] mb-2 uppercase tracking-wider">
                    Options * <span className="normal-case font-medium text-[#9CA3AF]">(select correct answer)</span>
                  </label>
                  <div className="space-y-2">
                    {form.options.map((opt, index) => (
                      <div key={opt.id} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="correctOption"
                          checked={form.correctOptionId === opt.id}
                          onChange={() => setForm((prev) => ({ ...prev, correctOptionId: opt.id }))}
                          className="shrink-0"
                          title="Mark as correct"
                        />
                        <div className="md-outlined-textfield flex-1">
                          <input
                            type="text"
                            value={opt.label}
                            onChange={(e) => {
                              const label = e.target.value;
                              setForm((prev) => ({
                                ...prev,
                                options: prev.options.map((o) =>
                                  o.id === opt.id ? { ...o, label } : o
                                ),
                              }));
                            }}
                            className="w-full"
                            placeholder={`Option ${index + 1}`}
                          />
                        </div>
                        {form.options.length > 2 && (
                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => {
                                const next = prev.options.filter((o) => o.id !== opt.id);
                                const correctStillExists = next.some((o) => o.id === prev.correctOptionId);
                                return {
                                  ...prev,
                                  options: next,
                                  correctOptionId: correctStillExists ? prev.correctOptionId : next[0]?.id || '',
                                };
                              });
                            }}
                            className="text-xs text-[#EF4444] font-semibold px-2"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        options: [...prev.options, { id: newOptionId(), label: '' }],
                      }))
                    }
                    className="mt-2 text-[13px] text-[#18A5F9] hover:text-[#0D8FDE] font-semibold"
                  >
                    + Add option
                  </button>
                </div>
              )}

              <label className="flex items-center gap-2 text-sm font-medium text-[#374151]">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                  className="rounded border-[#ECEEF1]"
                />
                Active
              </label>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditing(null);
                    setError('');
                  }}
                  className="md-btn md-btn-outlined flex-1"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="md-btn md-btn-primary flex-1" disabled={isSaving}>
                  {isSaving ? 'Saving…' : editing ? 'Save Changes' : 'Create Question'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QualityQuestionsPage;
